import React from "react";
import { prepFqReportStyles } from "../styles/prepFqReportStyles";
import { useTranslation } from "next-i18next";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import clsx from "clsx";
import { PrepFqResult } from "../../../models/users";
import { Button } from "@mui/material";
import { dynamicSort, removeDuplicatesFromArray } from "../../../utils/array";
import Highlighter from "react-highlight-words";
import Sticky from "react-stickynode";
import useMediaQuery from "@mui/material/useMediaQuery";
import LeavesIcon from "../icons/LeavesIcon";
import MinusCircleIcon from "../icons/MinusCircleIcon";
import AddCircleIcon from "../icons/AddCircleIcon";
import ExclamationCircleIcon from "../icons/ExclamationCircleIcon";
import ThermometerContents from "./ThermometerContents";
import { getAsterisks } from "../fetchers/helpers";
import { useAuth } from "../../../firebase/useAuth";
import useUser from "../../../firebase/useUser";
import usePrepFqProfile from "../fetchers/usePrepFqProfile";
import { useResizeDetector } from "react-resize-detector";
import useAveragePrepFqScores from "../fetchers/useAveragePrepFqScores";

interface Props {
  suggestionsVisible: boolean;
  animate: boolean;
  topicModalOpen: boolean;
  setTopicModalOpen(boolean): any;
  setBcgModalOpen(boolean): any;
  setSelectedModuleId(string): any;
}

const SuggestionsSection: React.FC<Props> = ({
  suggestionsVisible,
  animate,
  setTopicModalOpen,
  topicModalOpen,
  setSelectedModuleId,
  setBcgModalOpen,
}) => {
  const classes = prepFqReportStyles();
  const { t } = useTranslation(["prepFq"]);
  const matched = useMediaQuery("(max-width:960px)");
  const authUser = useAuth();
  const { user } = useUser(authUser?.user?.uid);
  const { profile, isLoading: profileLoading } = usePrepFqProfile(
    user?.user_id
  );
  const { avgScores, isLoading: averagesLoading } = useAveragePrepFqScores(
    profile?.prep_fq_profile_id,
    user,
    profile
  );
  const { height: rightColHeight, ref: rightColRef } = useResizeDetector();
  const isLoading = profileLoading || averagesLoading;
  const openBcgModal = () => {
    setSelectedModuleId("behaviour_change_guide");
    setBcgModalOpen(true);
  };
  const toggleTopicModal = (suggestion: any, index: number) => (e) => {
    if (topicModalOpen && index === 0) {
      setSelectedModuleId(suggestion.module_id);
      setTopicModalOpen(true);
    } else if (!topicModalOpen) {
      setSelectedModuleId(suggestion.module_id);
      setTopicModalOpen(true);
    }
  };

  if (!profile) {
    return null;
  }
  return (
    <React.Fragment>
      <Grid
        container
        direction="row"
        alignContent="flex-start"
        justifyContent="flex-start"
      >
        <Grid xs={12} sm={12} md={4} lg={4} item>
          <Grid
            justifyContent="space-between"
            container
            style={{ height: "100%" }}
          >
            <Grid className={classes.thermometerColumn} item xs={12}>
              <Paper
                elevation={0}
                className={clsx(classes.borderedCard, classes.thermometerCard)}
                style={{ border: 0, padding: "12px 0px" }}
              >
                <Sticky
                  bottomBoundary="#little-improvement"
                  enabled={!(matched || topicModalOpen)}
                >
                  <div>
                    <h2 className={classes.thermometerTitle}>
                      {t("prepFq:pdf.thermometer.title")}
                    </h2>
                    <ThermometerContents
                      suggestionsVisible={suggestionsVisible}
                      animate={animate}
                      showDownloadButton
                      profile={profile}
                      avgScores={avgScores}
                      loading={isLoading}
                    />
                  </div>
                </Sticky>
              </Paper>
            </Grid>
            <Grid
              style={{ display: "flex", alignItems: "flex-end" }}
              item
              xs={12}
            >
              <Paper
                elevation={0}
                className={clsx(classes.borderedCard, "nineteenth-step-bcg")}
                style={{ marginBottom: matched ? 72 : 0 }}
              >
                <div className={classes.suggestionIconContainer}>
                  <LeavesIcon
                    className={clsx(
                      classes.suggestionIconSize,
                      classes.lotsIcon
                    )}
                  />
                </div>
                <h3 className={classes.suggestionsTitle}>
                  {t("prepFq:report.suggestions.adoptingLifeStyle")}
                </h3>
                <p className={classes.promptText}>
                  {t("prepFq:report.suggestions.adoptingLifeStyleDescription")}
                </p>
                <div className={clsx(classes.goButtonContainer)}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={openBcgModal}
                  >
                    {t("common:buttons.go")}
                  </Button>
                </div>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
        <Grid xs={12} sm={12} md={8} lg={8} item>
          <Grid item ref={rightColRef} className={classes.rightCol}>
            <div>
              <p className={clsx(classes.helpTextTop, "fifth-step-legend")}>
                {t("prepFq:report.suggestions.mortality")}&emsp;
                {t("prepFq:report.suggestions.qol")}&emsp;
                {t("prepFq:report.suggestions.practical")}
              </p>
              <Paper
                elevation={0}
                className={clsx(classes.borderedCard, "sixth-step-doing-well")}
              >
                <div className={classes.suggestionIconContainer}>
                  <AddCircleIcon
                    className={clsx(
                      classes.suggestionIconSize,
                      classes.wellIcon
                    )}
                  />
                </div>
                <h3 className={classes.suggestionsTitle}>
                  {t("prepFq:report.suggestions.doingWell")}
                </h3>
                <ul className={classes.suggestionsList}>
                  {profile?.suggestions?.doing_well.length === 0 ? (
                    <div className={classes.promptText}>
                      {t("prepFq:report.empty.well")}
                    </div>
                  ) : (
                    <React.Fragment>
                      {removeDuplicatesFromArray(
                        profile?.suggestions?.doing_well.slice(),
                        "suggestion"
                      )
                        .filter((suggestion) => suggestion.suggestion !== "")
                        .filter(
                          (suggestion) =>
                            suggestion.weighted_scoring.value === 3
                        )
                        .sort(dynamicSort("weighted_scoring", "value", "desc"))
                        .reverse()
                        .map((suggestion, index) => {
                          return (
                            <li
                              onClick={toggleTopicModal(suggestion, index)}
                              key={suggestion.question_id}
                              className={clsx(classes.suggestionsListItem, {
                                "tenth-step-topic-list-item": index === 0,
                              })}
                              id={
                                index === 0
                                  ? "tenth-step-topic-list-item__observe"
                                  : ""
                              }
                              style={{
                                cursor:
                                  index === 0 && topicModalOpen
                                    ? "pointer"
                                    : undefined,
                              }}
                            >
                              <div
                                className={clsx(
                                  classes.suggestionsListItemText,
                                  {
                                    [classes.suggestionsListItemTextAfter]:
                                      !topicModalOpen ||
                                      (topicModalOpen && index === 0),
                                  }
                                )}
                              >
                                {getAsterisks(
                                  suggestion.weighted_scoring.value
                                )}{" "}
                                <Highlighter
                                  searchWords={
                                    suggestion.highlights
                                      ? suggestion.highlights
                                      : [""]
                                  }
                                  autoEscape={true}
                                  textToHighlight={suggestion.suggestion}
                                />
                              </div>
                            </li>
                          );
                        })}
                      {removeDuplicatesFromArray(
                        profile?.suggestions?.doing_well.slice(),
                        "suggestion"
                      )
                        .filter((suggestion) => suggestion.suggestion !== "")
                        .filter(
                          (suggestion) =>
                            suggestion.weighted_scoring.value !== 3
                        )
                        .sort(dynamicSort("weighted_scoring", "value", "desc"))
                        .map((suggestion, index) => {
                          return (
                            <li
                              onClick={() => {
                                if (topicModalOpen && index === 0) {
                                  setSelectedModuleId(suggestion.module_id);
                                  setTopicModalOpen(true);
                                } else if (!topicModalOpen) {
                                  setSelectedModuleId(suggestion.module_id);
                                  setTopicModalOpen(true);
                                }
                              }}
                              key={suggestion.question_id}
                              className={clsx(classes.suggestionsListItem, {
                                "tenth-step-topic-list-item": index === 0,
                              })}
                              id={
                                index === 0
                                  ? "tenth-step-topic-list-item__observe"
                                  : ""
                              }
                              style={{
                                cursor:
                                  index === 0 && topicModalOpen
                                    ? "pointer"
                                    : undefined,
                              }}
                            >
                              <div
                                className={clsx(
                                  classes.suggestionsListItemText,
                                  {
                                    [classes.suggestionsListItemTextAfter]:
                                      !topicModalOpen ||
                                      (topicModalOpen && index === 0),
                                  }
                                )}
                              >
                                {getAsterisks(
                                  suggestion.weighted_scoring.value
                                )}{" "}
                                <Highlighter
                                  searchWords={
                                    suggestion.highlights
                                      ? suggestion.highlights
                                      : [""]
                                  }
                                  autoEscape={true}
                                  textToHighlight={suggestion.suggestion}
                                />
                              </div>
                            </li>
                          );
                        })}
                    </React.Fragment>
                  )}
                </ul>
              </Paper>
            </div>
            <Paper
              elevation={0}
              className={clsx(
                classes.borderedCard,
                "seventh-step-little-improvement"
              )}
              id="little-improvement"
            >
              <div className={classes.suggestionIconContainer}>
                <MinusCircleIcon
                  className={clsx(
                    classes.suggestionIconSize,
                    classes.littleIcon
                  )}
                />
              </div>
              <h3 className={classes.suggestionsTitle}>
                {t("prepFq:report.suggestions.littleImprovement")}
              </h3>
              <ul className={classes.suggestionsList}>
                {profile?.suggestions?.little_improvement.length === 0 ? (
                  <div className={classes.promptText}>
                    {t("prepFq:report.empty.little")}
                  </div>
                ) : (
                  removeDuplicatesFromArray(
                    profile?.suggestions?.little_improvement.slice(),
                    "suggestion"
                  )
                    .filter((suggestion) => suggestion.suggestion !== "")
                    .sort(dynamicSort("weighted_scoring", "value", "desc"))
                    .map((suggestion) => {
                      return (
                        <li
                          onClick={() => {
                            setSelectedModuleId(suggestion.module_id);
                            setTopicModalOpen(true);
                          }}
                          key={suggestion.question_id}
                          className={classes.suggestionsListItem}
                        >
                          <div
                            className={clsx(
                              classes.suggestionsListItemText,
                              classes.suggestionsListItemTextAfter
                            )}
                          >
                            {getAsterisks(suggestion.weighted_scoring.value)}{" "}
                            <Highlighter
                              searchWords={
                                suggestion.highlights
                                  ? suggestion.highlights
                                  : [""]
                              }
                              autoEscape={true}
                              textToHighlight={suggestion.suggestion}
                            />
                          </div>
                        </li>
                      );
                    })
                )}
              </ul>
            </Paper>
          </Grid>
          <Grid item className={classes.rightCol}>
            <div>
              <Paper
                elevation={0}
                className={clsx(
                  classes.borderedCard,
                  "eighth-step-lots-of-improvement"
                )}
                style={{ marginBottom: 0 }}
              >
                <div className={classes.suggestionIconContainer}>
                  <ExclamationCircleIcon
                    className={clsx(
                      classes.suggestionIconSize,
                      classes.lotsIcon
                    )}
                  />
                </div>
                <h3 className={classes.suggestionsTitle}>
                  {t("prepFq:report.suggestions.lotsOfImprovement")}
                </h3>
                <ul className={classes.suggestionsList}>
                  {profile?.suggestions?.lots_of_improvements.length === 0 ? (
                    <div className={classes.promptText}>
                      {t("prepFq:report.empty.lots")}
                    </div>
                  ) : (
                    removeDuplicatesFromArray(
                      profile?.suggestions?.lots_of_improvements.slice(),
                      "suggestion"
                    )
                      .filter((suggestion) => suggestion.suggestion !== "")
                      .sort(dynamicSort("weighted_scoring", "value", "desc"))
                      .map((suggestion: PrepFqResult) => {
                        return (
                          <li
                            onClick={() => {
                              setSelectedModuleId(suggestion.module_id);
                              setTopicModalOpen(true);
                            }}
                            key={suggestion.question_id}
                            className={classes.suggestionsListItem}
                          >
                            <div
                              className={clsx(
                                classes.suggestionsListItemText,
                                classes.suggestionsListItemTextAfter
                              )}
                            >
                              {getAsterisks(suggestion.weighted_scoring.value)}{" "}
                              <Highlighter
                                searchWords={
                                  suggestion.highlights
                                    ? suggestion.highlights
                                    : [""]
                                }
                                autoEscape={true}
                                textToHighlight={suggestion.suggestion}
                              />
                            </div>
                          </li>
                        );
                      })
                  )}
                </ul>
              </Paper>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default SuggestionsSection;
